// HelpDocumentView.tsx — renders one help document (core/models/help.ts).
//
// The two help pages used to be JSX prose. They are data now, so the app can
// read the same text over the API; this is the web renderer for that data.
// Blocks render in the order they are authored; runs become text and
// <Link>s; a call-out keeps its distinct block. Nothing here knows what the
// pages say.

import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, Callout } from '@/ui';
import { usePageTitle } from '@/hooks';
import type { HelpBlock, HelpDocument, HelpLine } from '@/core/models/help';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 76ch;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.display};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Lead = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Section = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const H2 = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const P = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textMuted};
`;

const List = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.55;
  color: ${({ theme }) => theme.color.textMuted};
`;

function Line({ line }: { line: HelpLine }) {
  return (
    <>
      {line.map((run, i) =>
        'href' in run ? (
          <Link key={i} to={run.href}>
            {run.label}
          </Link>
        ) : (
          <Fragment key={i}>{run.text}</Fragment>
        ),
      )}
    </>
  );
}

function Block({ block }: { block: HelpBlock }) {
  switch (block.kind) {
    case 'paragraph':
      return (
        <P>
          <Line line={block.runs} />
        </P>
      );
    case 'bullets':
      return (
        <List>
          {block.items.map((b, i) => (
            <li key={i}>
              <Line line={b} />
            </li>
          ))}
        </List>
      );
    case 'callout':
      return (
        <Callout>
          <Line line={block.runs} />
        </Callout>
      );
  }
}

export function HelpDocumentView({ doc }: { doc: HelpDocument }) {
  usePageTitle(`${doc.title} / TruXSocial`);

  return (
    <Page>
      <Title>{doc.title}</Title>
      <Lead>{doc.lead}</Lead>

      {doc.sections.map((s) => (
        <Section key={s.id} id={s.id}>
          <H2>{s.heading}</H2>
          {s.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </Section>
      ))}
    </Page>
  );
}
